import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  Drawer,
  BadgePill,
  EmptyState,
} from '@/components/campus';
import {
  Users,
  Plus,
  Loader2,
  GripVertical,
  Clock,
  School,
  Mail,
  Phone,
  MapPin,
  Calendar,
  BookOpen,
  Award,
  X,
  MessageSquare,
  UserMinus,
  UserPlus,
  ChevronRight,
  Send,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stage configuration (matching database pnm_stage_enum)
const STAGES = [
  { id: 'prospect', label: 'Prospect', color: 'soft-silver' },
  { id: 'contacted', label: 'Contacted', color: 'electric-ice-blue' },
  { id: 'rushed', label: 'Rushed', color: 'soft-amber' },
  { id: 'bid_extended', label: 'Bid Extended', color: 'lavender-purple' },
  { id: 'bid_accepted', label: 'Bid Accepted', color: 'cool-mint' },
  { id: 'pledged', label: 'Pledged', color: 'cool-mint' },
  { id: 'initiated', label: 'Initiated', color: 'cool-mint' },
];

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ==================== PNM CARD ====================
const PNMCard = ({ pnm, onClick, onDragStart }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, pnm)}
      onClick={() => onClick(pnm)}
      className="p-3 bg-obsidian/50 rounded-lg border border-white/[0.06] cursor-pointer hover:border-white/[0.12] transition-all group"
    >
      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 text-muted-text opacity-0 group-hover:opacity-100 cursor-grab mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-cloud-white truncate">
            {pnm.first_name} {pnm.last_name}
          </p>
          {pnm.school && (
            <p className="text-xs text-soft-silver truncate flex items-center gap-1 mt-1">
              <School className="w-3 h-3" />
              {pnm.school}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-text flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {pnm.days_in_stage}d
            </span>
            {pnm.gpa && (
              <BadgePill variant="default" className="text-xs">
                {pnm.gpa} GPA
              </BadgePill>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== KANBAN COLUMN ====================
const KanbanColumn = ({ stage, pnms, onCardClick, onDragStart, onDragOver, onDrop }) => {
  const stageConfig = STAGES.find(s => s.id === stage) || { label: stage, color: 'soft-silver' };
  
  return (
    <div
      className="flex-shrink-0 w-64 bg-graphite/30 rounded-xl border border-white/[0.06]"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage)}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={cn("w-2 h-2 rounded-full", `bg-${stageConfig.color}`)} />
            <span className="text-sm font-medium text-cloud-white">{stageConfig.label}</span>
          </div>
          <span className="text-xs text-muted-text bg-white/[0.05] px-2 py-0.5 rounded-full">
            {pnms.length}
          </span>
        </div>
      </div>
      
      {/* Cards */}
      <div className="p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-350px)] overflow-y-auto">
        {pnms.map(pnm => (
          <PNMCard
            key={pnm.id}
            pnm={pnm}
            onClick={onCardClick}
            onDragStart={onDragStart}
          />
        ))}
        {pnms.length === 0 && (
          <div className="text-center text-muted-text text-xs py-8">
            No prospects
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ADD PNM FORM ====================
const AddPNMForm = ({ onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    school: '',
    graduation_year: '',
    major: '',
    gpa: '',
    hometown: '',
    referral_source: '',
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    const data = { ...formData };
    if (data.graduation_year) data.graduation_year = parseInt(data.graduation_year);
    if (data.gpa) data.gpa = parseFloat(data.gpa);
    
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-soft-silver">First Name *</Label>
          <Input
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
            data-testid="pnm-first-name"
          />
          {errors.first_name && <p className="text-signal-red text-xs">{errors.first_name}</p>}
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">Last Name *</Label>
          <Input
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
            data-testid="pnm-last-name"
          />
          {errors.last_name && <p className="text-signal-red text-xs">{errors.last_name}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-soft-silver">Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">Phone</Label>
          <Input
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-soft-silver">School</Label>
        <Input
          value={formData.school}
          onChange={(e) => handleChange('school', e.target.value)}
          placeholder="University of..."
          className="bg-obsidian border-white/[0.08] text-cloud-white"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-soft-silver">Grad Year</Label>
          <Input
            type="number"
            value={formData.graduation_year}
            onChange={(e) => handleChange('graduation_year', e.target.value)}
            placeholder="2028"
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">Major</Label>
          <Input
            value={formData.major}
            onChange={(e) => handleChange('major', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">GPA</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="4"
            value={formData.gpa}
            onChange={(e) => handleChange('gpa', e.target.value)}
            placeholder="3.5"
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-soft-silver">Hometown</Label>
          <Input
            value={formData.hometown}
            onChange={(e) => handleChange('hometown', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">Referral Source</Label>
          <Input
            value={formData.referral_source}
            onChange={(e) => handleChange('referral_source', e.target.value)}
            placeholder="Rush event, referral, etc."
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          disabled={isLoading}
          data-testid="add-pnm-submit"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Prospect'}
        </Button>
      </div>
    </form>
  );
};

// ==================== PNM DETAIL DRAWER ====================
const PNMDetailDrawer = ({ pnm, onUpdate, onDrop, onConvert, onAddNote, onClose, isProcessing }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState('');
  const [dropReason, setDropReason] = useState('');
  const [showDropForm, setShowDropForm] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);

  useEffect(() => {
    if (pnm) {
      setEditData({
        first_name: pnm.first_name || '',
        last_name: pnm.last_name || '',
        email: pnm.email || '',
        phone: pnm.phone || '',
        school: pnm.school || '',
        graduation_year: pnm.graduation_year || '',
        major: pnm.major || '',
        gpa: pnm.gpa || '',
        hometown: pnm.hometown || '',
        referral_source: pnm.referral_source || '',
        current_stage: pnm.current_stage || 'interested',
      });
    }
  }, [pnm]);

  if (!pnm) return null;

  const handleSaveEdit = () => {
    const data = { ...editData };
    if (data.graduation_year) data.graduation_year = parseInt(data.graduation_year);
    if (data.gpa) data.gpa = parseFloat(data.gpa);
    onUpdate(pnm.id, data);
    setIsEditing(false);
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    onAddNote(pnm.id, newNote);
    setNewNote('');
  };

  const handleDrop = () => {
    if (!dropReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    onDrop(pnm.id, dropReason);
    setShowDropForm(false);
  };

  const currentStage = STAGES.find(s => s.id === pnm.current_stage);
  const canConvert = pnm.current_stage === 'initiated' && pnm.email;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-cloud-white">
            {pnm.first_name} {pnm.last_name}
          </h2>
          <div className="flex items-center gap-2 mt-2">
            <BadgePill variant={currentStage?.color === 'cool-mint' ? 'success' : 'default'}>
              {currentStage?.label || pnm.current_stage}
            </BadgePill>
            <span className="text-xs text-muted-text">{pnm.days_in_stage} days in stage</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setIsEditing(!isEditing)}>
          {isEditing ? <X className="w-4 h-4" /> : <ChevronRight className="w-4 h-4 rotate-90" />}
        </Button>
      </div>

      {/* Info / Edit Form */}
      <div className="space-y-4">
        {isEditing ? (
          <>
            {/* Stage Selector */}
            <div className="space-y-2">
              <Label className="text-soft-silver">Stage</Label>
              <Select
                value={editData.current_stage}
                onValueChange={(value) => setEditData(prev => ({ ...prev, current_stage: value }))}
              >
                <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-graphite border-white/[0.08]">
                  {STAGES.map(stage => (
                    <SelectItem key={stage.id} value={stage.id} className="text-cloud-white">
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input
                value={editData.first_name}
                onChange={(e) => setEditData(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="First Name"
                className="bg-obsidian border-white/[0.08] text-cloud-white"
              />
              <Input
                value={editData.last_name}
                onChange={(e) => setEditData(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder="Last Name"
                className="bg-obsidian border-white/[0.08] text-cloud-white"
              />
            </div>
            <Input
              value={editData.email}
              onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Email"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
            <Input
              value={editData.phone}
              onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Phone"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
            <Input
              value={editData.school}
              onChange={(e) => setEditData(prev => ({ ...prev, school: e.target.value }))}
              placeholder="School"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
            <div className="grid grid-cols-3 gap-3">
              <Input
                value={editData.graduation_year}
                onChange={(e) => setEditData(prev => ({ ...prev, graduation_year: e.target.value }))}
                placeholder="Year"
                className="bg-obsidian border-white/[0.08] text-cloud-white"
              />
              <Input
                value={editData.major}
                onChange={(e) => setEditData(prev => ({ ...prev, major: e.target.value }))}
                placeholder="Major"
                className="bg-obsidian border-white/[0.08] text-cloud-white"
              />
              <Input
                value={editData.gpa}
                onChange={(e) => setEditData(prev => ({ ...prev, gpa: e.target.value }))}
                placeholder="GPA"
                className="bg-obsidian border-white/[0.08] text-cloud-white"
              />
            </div>
            <Input
              value={editData.hometown}
              onChange={(e) => setEditData(prev => ({ ...prev, hometown: e.target.value }))}
              placeholder="Hometown"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />

            <Button onClick={handleSaveEdit} className="w-full bg-electric-ice-blue text-white" disabled={isProcessing}>
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </>
        ) : (
          <div className="space-y-3 p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            {pnm.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-soft-silver" />
                <span className="text-cloud-white">{pnm.email}</span>
              </div>
            )}
            {pnm.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-soft-silver" />
                <span className="text-cloud-white">{pnm.phone}</span>
              </div>
            )}
            {pnm.school && (
              <div className="flex items-center gap-2 text-sm">
                <School className="w-4 h-4 text-soft-silver" />
                <span className="text-cloud-white">{pnm.school}</span>
              </div>
            )}
            {pnm.graduation_year && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-soft-silver" />
                <span className="text-cloud-white">Class of {pnm.graduation_year}</span>
              </div>
            )}
            {pnm.major && (
              <div className="flex items-center gap-2 text-sm">
                <BookOpen className="w-4 h-4 text-soft-silver" />
                <span className="text-cloud-white">{pnm.major}</span>
              </div>
            )}
            {pnm.gpa && (
              <div className="flex items-center gap-2 text-sm">
                <Award className="w-4 h-4 text-soft-silver" />
                <span className="text-cloud-white">{pnm.gpa} GPA</span>
              </div>
            )}
            {pnm.hometown && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-soft-silver" />
                <span className="text-cloud-white">{pnm.hometown}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notes Log */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-soft-silver flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Notes ({pnm.notes_log?.length || 0})
        </h4>
        
        {/* Add Note */}
        <div className="flex gap-2">
          <Input
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note..."
            className="bg-obsidian border-white/[0.08] text-cloud-white flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
          />
          <Button
            onClick={handleAddNote}
            size="icon"
            className="bg-electric-ice-blue text-white"
            disabled={!newNote.trim() || isProcessing}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>

        {/* Notes List */}
        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          {(pnm.notes_log || []).slice().reverse().map((note, idx) => (
            <div key={idx} className="p-3 bg-obsidian/30 rounded-lg border border-white/[0.04]">
              <p className="text-cloud-white text-sm">{note.note}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-text">
                <span>{note.author_name || 'Unknown'}</span>
                <span>•</span>
                <span>{formatDate(note.timestamp)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3 pt-4 border-t border-white/[0.06]">
        {/* Convert to Member (when initiated) */}
        {canConvert && !showConvertConfirm && (
          <Button
            onClick={() => setShowConvertConfirm(true)}
            className="w-full bg-cool-mint text-obsidian hover:bg-cool-mint/90"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Convert to Member
          </Button>
        )}

        {showConvertConfirm && (
          <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20 space-y-3">
            <p className="text-cloud-white text-sm">
              This will send an invitation to <strong>{pnm.email}</strong> to join as a member.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConvertConfirm(false)}
                className="flex-1 border-white/[0.08]"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => onConvert(pnm.id)}
                className="flex-1 bg-cool-mint text-obsidian"
                disabled={isProcessing}
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invite'}
              </Button>
            </div>
          </div>
        )}

        {/* Drop PNM */}
        {!showDropForm && !pnm.dropped && (
          <Button
            onClick={() => setShowDropForm(true)}
            variant="outline"
            className="w-full border-signal-red/50 text-signal-red hover:bg-signal-red/10"
          >
            <UserMinus className="w-4 h-4 mr-2" />
            Drop Prospect
          </Button>
        )}

        {showDropForm && (
          <div className="p-4 bg-signal-red/10 rounded-lg border border-signal-red/20 space-y-3">
            <Label className="text-soft-silver">Reason for dropping</Label>
            <Input
              value={dropReason}
              onChange={(e) => setDropReason(e.target.value)}
              placeholder="e.g., Not interested, GPA requirement, etc."
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDropForm(false)}
                className="flex-1 border-white/[0.08]"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleDrop}
                className="flex-1 bg-signal-red text-white"
                disabled={isProcessing}
              >
                {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Drop'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN RECRUITMENT PAGE ====================
export const Recruitment = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();
  const isAdmin = hasOrgRole(['owner', 'admin']);

  // State
  const [pipeline, setPipeline] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedPNM, setSelectedPNM] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [draggedPNM, setDraggedPNM] = useState(null);

  // Fetch pipeline
  const fetchPipeline = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/rush/pipeline/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setPipeline(data.pipeline || {});
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  // Add PNM
  const handleAddPNM = async (data) => {
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/rush/pnm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          ...data,
          organization_id: organization.id,
        }),
      });

      if (res.ok) {
        toast.success('Prospect added!');
        setIsAddOpen(false);
        fetchPipeline();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add prospect');
      }
    } catch (error) {
      toast.error('Failed to add prospect');
    } finally {
      setIsSubmitting(false);
    }
  };

  // View PNM detail
  const handleViewPNM = async (pnm) => {
    try {
      const res = await fetch(`${API_URL}/api/rush/pnm/${pnm.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSelectedPNM(data.pnm);
        setIsDetailOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch PNM:', error);
    }
  };

  // Update PNM
  const handleUpdatePNM = async (pnmId, updates) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/rush/pnm/${pnmId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(updates),
      });

      if (res.ok) {
        toast.success('Prospect updated!');
        const data = await res.json();
        setSelectedPNM(data.pnm);
        fetchPipeline();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update prospect');
      }
    } catch (error) {
      toast.error('Failed to update prospect');
    } finally {
      setIsProcessing(false);
    }
  };

  // Add note
  const handleAddNote = async (pnmId, note) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/rush/pnm/${pnmId}/note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ note }),
      });

      if (res.ok) {
        toast.success('Note added!');
        const data = await res.json();
        setSelectedPNM(data.pnm);
      } else {
        toast.error('Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setIsProcessing(false);
    }
  };

  // Drop PNM
  const handleDropPNM = async (pnmId, reason) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/rush/pnm/${pnmId}/drop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ reason }),
      });

      if (res.ok) {
        toast.success('Prospect dropped');
        setIsDetailOpen(false);
        setSelectedPNM(null);
        fetchPipeline();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to drop prospect');
      }
    } catch (error) {
      toast.error('Failed to drop prospect');
    } finally {
      setIsProcessing(false);
    }
  };

  // Convert to member
  const handleConvertPNM = async (pnmId) => {
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/rush/pnm/${pnmId}/convert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Invite sent!');
        setIsDetailOpen(false);
        setSelectedPNM(null);
        fetchPipeline();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to convert prospect');
      }
    } catch (error) {
      toast.error('Failed to convert prospect');
    } finally {
      setIsProcessing(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, pnm) => {
    setDraggedPNM(pnm);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    if (!draggedPNM || draggedPNM.current_stage === newStage) {
      setDraggedPNM(null);
      return;
    }

    await handleUpdatePNM(draggedPNM.id, { current_stage: newStage });
    setDraggedPNM(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="lock"
          title="Access Restricted"
          description="Only admins can access the recruitment pipeline."
        />
      </div>
    );
  }

  const totalActive = stats?.total_active || 0;

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      <PageHeader
        title="Rush Pipeline"
        subtitle={`${totalActive} active prospects`}
        actions={
          <Button
            onClick={() => setIsAddOpen(true)}
            className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
            data-testid="add-pnm-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Prospect
          </Button>
        }
      />

      {/* Stats */}
      {stats && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          <div className="flex-shrink-0 px-4 py-2 bg-graphite/50 rounded-lg border border-white/[0.06]">
            <p className="text-2xl font-semibold text-cloud-white">{stats.total_active}</p>
            <p className="text-xs text-soft-silver">Active</p>
          </div>
          <div className="flex-shrink-0 px-4 py-2 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
            <p className="text-2xl font-semibold text-cool-mint">{stats.converted_count}</p>
            <p className="text-xs text-soft-silver">Converted</p>
          </div>
          <div className="flex-shrink-0 px-4 py-2 bg-signal-red/10 rounded-lg border border-signal-red/20">
            <p className="text-2xl font-semibold text-signal-red">{stats.dropped_count}</p>
            <p className="text-xs text-soft-silver">Dropped</p>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGES.map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage.id}
              pnms={pipeline[stage.id] || []}
              onCardClick={handleViewPNM}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      </div>

      {/* Add PNM Drawer */}
      <Drawer
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Prospect"
        width="md"
      >
        <AddPNMForm
          onSubmit={handleAddPNM}
          onCancel={() => setIsAddOpen(false)}
          isLoading={isSubmitting}
        />
      </Drawer>

      {/* PNM Detail Drawer */}
      <Drawer
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedPNM(null);
        }}
        title="Prospect Details"
        width="lg"
      >
        <PNMDetailDrawer
          pnm={selectedPNM}
          onUpdate={handleUpdatePNM}
          onDrop={handleDropPNM}
          onConvert={handleConvertPNM}
          onAddNote={handleAddNote}
          onClose={() => setIsDetailOpen(false)}
          isProcessing={isProcessing}
        />
      </Drawer>
    </div>
  );
};

export default Recruitment;
